import React, { useState } from 'react';
import Box from '../../components/Common/Box';

interface AlertProps {
    alerts: any[];
    stocks: any[];
    loading: boolean;
    saving: boolean;
    error: string;
    selectedStock: string;
    setSelectedStock: (v: string) => void;
    alertType: 'PRICE_ABOVE' | 'PRICE_BELOW' | 'PERCENT_CHANGE';
    setAlertType: (v: 'PRICE_ABOVE' | 'PRICE_BELOW' | 'PERCENT_CHANGE') => void;
    thresholdValue: string;
    setThresholdValue: (v: string) => void;
    handleCreateAlert: (e: React.FormEvent) => void;
    handleDeleteAlert: (id: number) => void;
    handleToggleActive: (alert: any) => void;
    getAlertTypeLabel: (type: string) => string;
    formatThreshold: (type: string, value: number) => string;
}

const MobileAlertSettings: React.FC<AlertProps> = ({
    alerts, stocks, loading, saving, error,
    selectedStock, setSelectedStock, alertType, setAlertType,
    thresholdValue, setThresholdValue, handleCreateAlert,
    handleDeleteAlert, handleToggleActive, getAlertTypeLabel, formatThreshold
}) => {
    const [showForm, setShowForm] = useState(false);

    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '14px', borderRadius: '10px',
        border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-input)',
        fontSize: '15px', color: 'var(--text-heading)', boxSizing: 'border-box',
        minHeight: '48px',
    };

    const labelStyle: React.CSSProperties = {
        display: 'block', marginBottom: '6px', color: 'var(--text-muted)', fontSize: '13px', fontWeight: '600',
    };

    const activeAlerts = alerts.filter(a => !a.is_triggered);
    const triggeredAlerts = alerts.filter(a => a.is_triggered);

    return (
        <div style={{ paddingBottom: '30px', background: 'var(--bg-main)' }}>

            {/* Summary Header */}
            <div style={{ padding: '20px', background: 'var(--bg-box)', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'center', gap: '24px' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>🔔 Actives</div>
                    <div style={{ fontSize: '28px', fontWeight: '900', color: 'var(--text-heading)' }}>{activeAlerts.length}</div>
                </div>
                <div style={{ width: '1px', height: '40px', background: 'var(--border-color)' }} />
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>✅ Déclenchées</div>
                    <div style={{ fontSize: '28px', fontWeight: '900', color: '#4ade80' }}>{triggeredAlerts.length}</div>
                </div>
            </div>

            <div style={{ padding: '16px' }}>

                {/* Toggle Form Button */}
                <button
                    onClick={() => setShowForm(!showForm)}
                    style={{
                        width: '100%', padding: '14px', borderRadius: '12px', fontSize: '15px',
                        fontWeight: '600', cursor: 'pointer', border: 'none', minHeight: '48px',
                        background: showForm ? 'var(--bg-input)' : '#667eea', color: showForm ? 'var(--text-main)' : 'white',
                        marginBottom: '16px', touchAction: 'manipulation',
                        boxShadow: showForm ? 'none' : '0 4px 6px rgba(102, 126, 234, 0.25)',
                        transition: 'all 0.2s',
                    }}
                >
                    {showForm ? '✕ Fermer le formulaire' : '➕ Nouvelle alerte'}
                </button>

                {/* Create Alert Form (Collapsible) */}
                {showForm && (
                    <Box>
                        <div style={{ padding: '16px' }}>
                            {error && (
                                <div style={{ padding: '10px', marginBottom: '12px', backgroundColor: '#fee2e2', color: '#b91c1c', borderRadius: '8px', fontSize: '13px' }}>
                                    {error}
                                </div>
                            )}
                            <form onSubmit={(e) => { handleCreateAlert(e); setShowForm(false); }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={labelStyle}>Société</label>
                                        <select value={selectedStock} onChange={(e) => setSelectedStock(e.target.value)} style={inputStyle}>
                                            {stocks.map((stock: any) => (
                                                <option key={stock.symbole} value={stock.symbole}>{stock.symbole} - {stock.nom_societe}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Type d'alerte</label>
                                        <select value={alertType} onChange={(e) => setAlertType(e.target.value as any)} style={inputStyle}>
                                            <option value="PRICE_ABOVE">📈 Prix supérieur à</option>
                                            <option value="PRICE_BELOW">📉 Prix inférieur à</option>
                                            <option value="PERCENT_CHANGE">📊 Variation {'>'} (%)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>
                                            {alertType === 'PERCENT_CHANGE' ? 'Seuil (%)' : 'Prix seuil (FCFA)'}
                                        </label>
                                        <input
                                            type="number" value={thresholdValue}
                                            onChange={(e) => setThresholdValue(e.target.value)}
                                            placeholder={alertType === 'PERCENT_CHANGE' ? 'Ex: 5' : 'Ex: 15000'}
                                            step={alertType === 'PERCENT_CHANGE' ? '0.5' : '100'}
                                            min="0" style={inputStyle} required
                                        />
                                    </div>
                                </div>
                                <button
                                    type="submit" disabled={saving || !thresholdValue}
                                    style={{
                                        width: '100%', padding: '14px', borderRadius: '10px', fontSize: '15px',
                                        fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer',
                                        border: 'none', minHeight: '48px', background: '#48bb78', color: 'white',
                                        opacity: (saving || !thresholdValue) ? 0.6 : 1,
                                        boxShadow: '0 4px 6px rgba(72, 187, 120, 0.25)',
                                    }}
                                >
                                    {saving ? 'Création...' : '🔔 Créer l\'alerte'}
                                </button>
                            </form>
                        </div>
                    </Box>
                )}

                {/* Alerts List */}
                <div style={{ marginTop: '8px' }}>
                    <div style={{ fontSize: '15px', fontWeight: 'bold', color: 'var(--text-heading)', marginBottom: '12px' }}>
                        📋 Mes alertes ({alerts.length})
                    </div>

                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Chargement...</div>
                    ) : alerts.length === 0 ? (
                        <Box>
                            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                <p style={{ fontSize: '40px', marginBottom: '8px' }}>🔕</p>
                                <p style={{ fontSize: '15px' }}>Aucune alerte configurée</p>
                                <p style={{ fontSize: '13px', marginTop: '8px' }}>Appuyez sur "Nouvelle alerte" pour commencer</p>
                            </div>
                        </Box>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {alerts.map((alert: any) => (
                                <Box key={alert.id}>
                                    <div style={{ padding: '14px 16px', opacity: alert.is_triggered ? 0.65 : 1 }}>
                                        {/* Row 1: Symbol + Status */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <span style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--text-heading)' }}>
                                                {alert.stock_symbol}
                                            </span>
                                            {alert.is_triggered ? (
                                                <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>✅ Déclenché</span>
                                            ) : (
                                                <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>⏳ En attente</span>
                                            )}
                                        </div>

                                        {/* Row 2: Condition */}
                                        <div style={{ fontSize: '14px', color: 'var(--text-main)', marginBottom: '10px' }}>
                                            {getAlertTypeLabel(alert.alert_type)} <strong>{formatThreshold(alert.alert_type, alert.threshold_value)}</strong>
                                        </div>

                                        {/* Row 3: Actions */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color)', paddingTop: '10px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minHeight: '44px' }}>
                                                <input
                                                    type="checkbox" checked={alert.is_active}
                                                    onChange={() => handleToggleActive(alert)}
                                                    disabled={alert.is_triggered}
                                                    style={{ width: '22px', height: '22px', accentColor: '#48bb78' }}
                                                />
                                                <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                                                    {alert.is_active ? 'Active' : 'Désactivée'}
                                                </span>
                                            </label>
                                            <button
                                                onClick={() => handleDeleteAlert(alert.id)}
                                                style={{
                                                    background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)',
                                                    color: '#ef4444', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                                                    padding: '8px 16px', borderRadius: '8px', minHeight: '44px',
                                                    touchAction: 'manipulation',
                                                }}
                                            >
                                                🗑️ Supprimer
                                            </button>
                                        </div>
                                    </div>
                                </Box>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info Box */}
                <div style={{ marginTop: '20px' }}>
                    <Box>
                        <div style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <span style={{ fontSize: '24px' }}>💡</span>
                                <div>
                                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: 'var(--text-heading)' }}>Comment ça marche ?</h4>
                                    <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--text-muted)', lineHeight: '1.7', fontSize: '13px' }}>
                                        <li>Alertes vérifiées quotidiennement à 18h30</li>
                                        <li>Notification email + in-app</li>
                                        <li>Auto-désactivation après déclenchement</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </Box>
                </div>
            </div>
        </div>
    );
};

export default MobileAlertSettings;
